import React from "react";
import { useTranslation } from "react-i18next";

interface ErrorComponentProps {
  error: unknown;
}

function getHttpErrorMessage(t: (key: string) => string, status: number): string {
  switch (status) {
    case 400:
      return t("error.http.400");
    case 401:
      return t("error.http.401");
    case 403:
      return t("error.http.403");
    case 404:
      return t("error.http.404");
    case 408:
      return t("error.http.408");
    case 500:
      return t("error.http.500");
    case 502:
    case 503:
    case 504:
      return t("error.http.5xx_gateway");
    default:
      if (status >= 400 && status < 500) {
        return t("error.http.4xx");
      }
      if (status >= 500) {
        return t("error.http.5xx");
      }
      return t("error.unexpected");
  }
}

export const ErrorComponent: React.FC<ErrorComponentProps> = ({ error }) => {
  const { t } = useTranslation();
  let message = t("error.unexpected");
  let details: string | undefined;

  // HTTP error handling
  if (error instanceof Response) {
    message = getHttpErrorMessage(t, error.status);
    details = `HTTP ${error.status} ${error.statusText}`;
  } else if (error && typeof error === "object" && "status" in error && typeof (error as any).status === "number") {
    message = getHttpErrorMessage(t, (error as any).status);
    details = `HTTP ${String((error as any).status)} ${(error as any).statusText || ""}`;
  } else if (error instanceof Error) {
    message = error.message;
    details = error.stack;
  } else if (typeof error === "string") {
    message = error;
  }

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = "/";
    }
  };

  return (
    <div className="bg-red-50 border border-red-300 text-red-800 px-6 py-6 rounded-lg mt-8 max-w-xl mx-auto flex flex-col items-center shadow" role="alert" aria-live="polite">
      <svg className="w-12 h-12 mb-2 text-red-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
      </svg>
      <h2 className="text-2xl font-bold mb-2">{t("error.title")}</h2>
      <p id="error-message" className="mb-4 text-center">{message || t("error.fallback")}</p>
      <button
        onClick={handleBack}
        className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-400"
        aria-label={t("error.back")}
      >
        {t("error.back")}
      </button>
      {import.meta.env.DEV && details && (
        <pre className="mt-4 text-xs bg-red-100 p-2 rounded w-full overflow-x-auto whitespace-pre-wrap text-left">{details}</pre>
      )}
    </div>
  );
};

// Translation keys to add:
// error.title: "Etwas ist schief gelaufen"
// error.fallback: "Bitte versuchen Sie es später erneut oder wenden Sie sich an den Support."
// error.back: "Zurück zur vorherigen Seite"
// error.unexpected: "Ein unerwarteter Fehler ist aufgetreten."
// error.http.400: "Die Anfrage war fehlerhaft. Bitte überprüfen Sie Ihre Eingaben."
// error.http.401: "Sie sind nicht berechtigt, diese Seite zu sehen. Bitte melden Sie sich an."
// error.http.403: "Sie haben keine Berechtigung, auf diese Seite zuzugreifen."
// error.http.404: "Die angeforderte Seite oder Ressource wurde nicht gefunden."
// error.http.500: "Ein Serverfehler ist aufgetreten. Bitte versuchen Sie es später erneut."
// error.http.5xx_gateway: "Der Server ist derzeit nicht erreichbar. Bitte versuchen Sie es später erneut."
// error.http.4xx: "Ein Fehler ist bei Ihrer Anfrage aufgetreten."
// error.http.5xx: "Ein Fehler auf dem Server ist aufgetreten." 